import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { SERVER_URL } from "../../config/config";
import { Helmet } from "react-helmet-async";
import NotFound from "../NotFound";

// Function to process HTML content and style highlight tags
const processHighlightTags = (htmlContent) => {
  if (!htmlContent) return "";

  // Replace <highlight> tags (case-insensitive) with styled divs
  let processedContent = htmlContent;

  // First, handle HTML encoded tags (if they exist)
  processedContent = processedContent.replace(
    /&lt;highlight\s*&gt;/gi,
    "<highlight>",
  );
  processedContent = processedContent.replace(
    /&lt;\/highlight\s*&gt;/gi,
    "</highlight>",
  );

  // Replace highlight tags - handle multiple cases (non-greedy match)
  processedContent = processedContent.replace(
    /<highlight\s*>([\s\S]*?)<\/highlight>/gi,
    '<div class="blog-highlight-section">$1</div>',
  );

  // Also handle tags that might be in paragraph tags
  processedContent = processedContent.replace(
    /<p>\s*<highlight\s*>([\s\S]*?)<\/highlight>\s*<\/p>/gi,
    '<div class="blog-highlight-section">$1</div>',
  );

  // Handle cases where highlight is inside paragraph with content before/after
  processedContent = processedContent.replace(
    /<p>([^<]*)<highlight\s*>([\s\S]*?)<\/highlight>([^<]*)<\/p>/gi,
    '<p>$1</p><div class="blog-highlight-section">$2</div><p>$3</p>',
  );

  return processedContent;
};

// Function to format date from ISO string to "Day Month Year" format
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()} `;
};

function BlogDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect if slug has trailing hyphens to maintain clean URLs
  useEffect(() => {
    if (slug && /-+(?=$|#|\?)/.test(slug)) {
      const cleanSlug = slug.replace(/-+$/, '');
      navigate(`/blogs/${cleanSlug}`, { replace: true });
    }
  }, [slug, navigate]);

  useEffect(() => {
    const loadBlog = async () => {
      try {
        setLoading(true);

        // Check if blog data was passed from parent component
        if (location.state?.blogData) {
          setBlog(location.state.blogData);
          setLoading(false);
          return;
        }

        // Fallback: Fetch from API if data not passed (for direct URL access)
        const response = await fetch(`${SERVER_URL}/api/blog/blogForWebside`);
        const result = await response.json();

        if (result.data && Array.isArray(result.data)) {
          // Find blog by slug (normalized to ignore trailing hyphens)
          const foundBlog = result.data.find((b) => {
            if (!b.slug) return false;
            const cleanBSlug = b.slug.replace(/-+$/, '');
            const cleanParamSlug = slug ? slug.replace(/-+$/, '') : '';
            return cleanBSlug === cleanParamSlug;
          });
          if (foundBlog) {
            setBlog(foundBlog);
          } else {
            setError("Blog not found");
          }
        } else {
          setError("Failed to load blog");
        }
      } catch (err) {
        console.error("Error fetching blog:", err);
        setError("Error loading blog");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadBlog();
    }
  }, [slug, location.state]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-2xl font-bold text-[#1C486B] mb-4">
            Loading blog...
          </div>
        </div>
      </div>
    );
  }

  if (error === "Blog not found" || (!loading && !blog && !error)) {
    return <NotFound />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center p-8 max-w-md bg-white rounded-3xl shadow-xl border border-gray-100/80">
          <div className="flex justify-center mb-6">
            <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-red-50 text-red-500">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-[#1C486B] tracking-tight mb-2">
            Unable to Load Blog
          </h2>
          <p className="text-[#667085] text-sm md:text-base leading-relaxed mb-6">
            {error || "We encountered an unexpected error while trying to fetch the blog post. Please check your connection and try again."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              onClick={() => window.location.reload()}
              className="w-full sm:w-auto px-6 py-2.5 rounded-full font-semibold text-white text-sm bg-[#1C486B] hover:bg-[#14344E] active:scale-95 transition-all shadow-md shadow-[#1c486b]/10 hover:shadow-lg"
            >
              Retry
            </button>
            <Link
              to="/blogs"
              className="w-full sm:w-auto px-6 py-2.5 rounded-full font-semibold text-[#1C486B] bg-gray-50 hover:bg-gray-100 active:scale-95 border border-gray-200 transition-all text-sm text-center"
            >
              Back to blogs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formattedDate = formatDate(blog.updatedAt);
  const authorName = blog.authorname || "Nirantara";
  const metaDescription = blog.meta?.description || blog.subheading || "";
  const coverImage =
    Array.isArray(blog.coverImage) && blog.coverImage.length > 0
      ? blog.coverImage[0]
      : "/blog-banner.jpg";

  return (
    <div className="flex flex-col items-center w-full min-h-screen bg-white">
      <Helmet>
        <title>{blog.title} | Nirantara</title>
        <meta name="description" content={metaDescription} />
        <meta name="author" content={authorName} />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={coverImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={coverImage} />
        <link rel="canonical" href={`https://nirantara.solutions/blogs/${blog.slug}`} />
      </Helmet>

      {/* Blog Hero section */}
      <div className="relative w-full h-[350px] md:h-[500px] lg:h-[600px] overflow-hidden flex items-end">
        <img
          src={coverImage}
          alt={blog.title}
          className="absolute inset-0 object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      {/* Blog Content Section */}
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-12 md:py-20">
        <div className="mb-8">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-3 text-[#6941C6] font-bold text-lg hover:underline transition-all"
          >
            <svg
              className="w-5 h-5 rotate-180"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
            Back
          </Link>
        </div>
        <div className="w-full">
          {/* Metadata */}
          <div className="mb-4">
            <div className="text-[#6941C6] font-semibold text-sm md:text-base uppercase tracking-wider">
              {authorName} • {formattedDate}
            </div>
          </div>

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {blog.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="text-xs font-semibold px-3 py-1 bg-gray-100 text-[#667085] rounded-full uppercase tracking-wider"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Main Title */}
          <h1 className="text-3xl md:text-5xl lg:text-[54px] font-bold text-[#101828] leading-[1.1] mb-12 uppercase tracking-tight max-w-6xl">
            {blog.title}
          </h1>

          {/* Prose Content */}
          <div className="flex flex-col gap-8 text-[#1D2939] text-lg md:text-xl leading-[1.6] max-w-6xl">
            {blog.subheading && (
              <section className="flex flex-col gap-6">
                <p className="text-2xl font-semibold text-[#1C486B]">
                  {blog.subheading}
                </p>
              </section>
            )}

            {blog.content && (
              <>
                <style>{`
                  .blog-content-wrapper .blog-highlight-section {
                    background-color: #F9FAFB !important;
                    border-left: 4px solid #6941C6 !important;
                    padding: 24px !important;
                    margin: 24px 0 !important;
                    font-style: italic !important;
                    color: #475467 !important;
                    border-radius: 0 4px 4px 0 !important;
                    line-height: 1.6 !important;
                    display: block !important;
                  }
                  .blog-content-wrapper .blog-highlight-section p {
                    margin: 0 !important;
                    color: #475467 !important;
                    font-style: italic !important;
                  }
                  .blog-content-wrapper .blog-highlight-section p:not(:last-child) {
                    margin-bottom: 12px !important;
                  }
                  .blog-content-wrapper .blog-highlight-section * {
                    color: #475467 !important;
                    font-style: italic !important;
                  }
                  .blog-content-wrapper .blog-highlight-section strong,
                  .blog-content-wrapper .blog-highlight-section b {
                    font-weight: 600 !important;
                    font-style: italic !important;
                  }
                `}</style>
                <section
                  className="flex flex-col gap-6 prose prose-lg max-w-none blog-content-wrapper"
                  dangerouslySetInnerHTML={{
                    __html: processHighlightTags(blog.content),
                  }}
                  style={{
                    color: "#1D2939",
                    lineHeight: "1.6",
                  }}
                />
              </>
            )}

            <div className="mt-16 mb-20">
              <Link
                to="/blogs"
                className="inline-flex items-center gap-3 text-[#6941C6] font-bold text-lg hover:underline transition-all"
              >
                <svg
                  className="w-5 h-5 rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
                Back to all blogs
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlogDetail;
